import { useEffect, useMemo, useRef, useState } from "react";

export default function SearchableMultiUserSelect({
  users = [],
  value = [],
  onChange,
  placeholder = "Search users...",
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedIds = Array.isArray(value) ? value.map(Number) : [];

  const selectedUsers = users.filter((u) => selectedIds.includes(Number(u.id)));

  useEffect(() => {
    function handleClickOutside(event) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();

    return users.filter((u) => {
      const isSelected = selectedIds.includes(Number(u.id));
      if (isSelected) return false;

      if (!q) return true;

      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();

      return name.includes(q) || email.includes(q);
    });
  }, [users, query, selectedIds]);

  const addUser = (userId) => {
    const next = [...selectedIds, Number(userId)];
    onChange(next);
    setQuery("");
    setIsOpen(false);
  };

  const removeUser = (userId) => {
    onChange(selectedIds.filter((id) => id !== Number(userId)));
  };

  return (
    <div ref={containerRef} className="space-y-2">
      <input
        type="text"
        className="border p-2 w-full"
        value={query}
        onFocus={() => setIsOpen(true)}
        onClick={() => setIsOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        placeholder={placeholder}
      />

      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedUsers.map((user) => (
            <button
              key={user.id}
              type="button"
              className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm"
              onClick={() => removeUser(user.id)}
              title="Remove user"
            >
              {user.name} ×
            </button>
          ))}
        </div>
      )}

      {isOpen && (
        <div className="border rounded max-h-48 overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <div className="p-2 text-sm text-gray-500">No users found</div>
          ) : (
            filteredUsers.map((user) => (
              <button
                key={user.id}
                type="button"
                className="block w-full text-left px-3 py-2 hover:bg-gray-100"
                onClick={() => addUser(user.id)}
              >
                <div className="font-medium">{user.name}</div>
                {user.email ? (
                  <div className="text-xs text-gray-500">{user.email}</div>
                ) : null}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}